/**
 * The save channel's store: what a hosted page asked the feed to remember, on this device.
 *
 * A page in the html viewer runs in an opaque origin with no storage of its own, and a card
 * BLITZ leaves is unloaded (WebCardHost.sleep), so without this every page is a one-session
 * card. The page calls window.blitz.save(obj) (the viewer's probe); the viewer relays the JSON
 * string up; WebCardHost hands it here. On the next load the host answers the viewer's
 * {blitz:'load'} from here and the probe gives the page window.blitz.load().
 *
 * The rules, all enforced here whatever the page or the viewer did before:
 *  - keyed by the card's id, blitz:state:<id>, so no card can read or overwrite another's;
 *  - at most 4096 bytes of UTF-8 per card; a bigger save is refused and the key left alone;
 *  - at most one write a second per card, trailing edge, last wins: a page that saves on every
 *    frame costs one setItem a second, and a read in between returns the pending value, so a
 *    card left and reopened within the second still gets its latest state;
 *  - every storage call in try/catch: private mode, a full quota or a storage that throws means
 *    the page simply starts fresh (null), never an error in the feed.
 * This device only (ruling 4). Saved state is the page's own word and never ranks, pays or
 * enters telemetry: nothing here is read by anything but the card that wrote it.
 */

export const STATE_KEY_PREFIX = 'blitz:state:';
export const STATE_MAX_BYTES = 4096;
export const STATE_WRITE_INTERVAL_MS = 1000;

/** The slice of Storage this module uses, so tests can hand in one that throws. */
export type StateStorage = Pick<Storage, 'getItem' | 'setItem'>;

export function stateKey(cardId: string): string {
  return STATE_KEY_PREFIX + cardId;
}

/** Length in bytes of a string as UTF-8, the unit the 4 KB cap is written in. */
export function utf8Bytes(s: string): number {
  return new TextEncoder().encode(s).length;
}

/** A string the channel will carry: at most STATE_MAX_BYTES of UTF-8. */
export function isStorableState(data: unknown): data is string {
  return typeof data === 'string' && utf8Bytes(data) <= STATE_MAX_BYTES;
}

function defaultStorage(): StateStorage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    // Reading `localStorage` itself throws where storage is blocked.
    return null;
  }
}

interface Pending {
  data: string;
  storage: StateStorage | null;
  timer: ReturnType<typeof setTimeout>;
}

/** Saves not yet written, by card id. A read returns these first: last wins. */
const pending = new Map<string, Pending>();
let pagehideBound = false;

function write(cardId: string, data: string, storage: StateStorage | null): boolean {
  if (!storage) return false;
  try {
    storage.setItem(stateKey(cardId), data);
    return true;
  } catch {
    return false;
  }
}

/** Write every pending save now: the feed is going away and the timers will not fire. */
export function flushCardState(): void {
  for (const [cardId, p] of pending) {
    clearTimeout(p.timer);
    write(cardId, p.data, p.storage);
  }
  pending.clear();
}

/** The card's saved state as the JSON string the page saved, or null when there is none. */
export function loadCardState(cardId: string, storage: StateStorage | null = defaultStorage()): string | null {
  const p = pending.get(cardId);
  if (p) return p.data;
  if (!storage) return null;
  try {
    const v = storage.getItem(stateKey(cardId));
    return isStorableState(v) ? v : null;
  } catch {
    return null;
  }
}

/**
 * Remember `data` for the card. Refused (false, nothing written, nothing pending changed) when
 * it is not a string of at most 4096 bytes. Otherwise it is written at the end of a one-second
 * window opened by the first save since the last write; saves inside the window replace it.
 */
export function saveCardState(cardId: string, data: unknown, storage: StateStorage | null = defaultStorage()): boolean {
  if (!isStorableState(data)) return false;
  const p = pending.get(cardId);
  if (p) {
    p.data = data;
    p.storage = storage;
    return true;
  }
  const timer = setTimeout(() => {
    const due = pending.get(cardId);
    if (!due) return;
    pending.delete(cardId);
    write(cardId, due.data, due.storage);
  }, STATE_WRITE_INTERVAL_MS);
  pending.set(cardId, { data, storage, timer });
  if (!pagehideBound && typeof addEventListener === 'function') {
    pagehideBound = true;
    addEventListener('pagehide', flushCardState);
  }
  return true;
}

/** Tests only: drop every pending save without writing it. */
export function resetCardStateForTests(): void {
  for (const p of pending.values()) clearTimeout(p.timer);
  pending.clear();
}
